import {
  FinalizarSimulacionRequestSchema,
  FinalizarSimulacionResponseSchema,
  IniciarSimulacionResponseSchema,
  MiSimulacionResponseSchema,
  MisAsignacionesResponseSchema,
  OkResponseSchema,
  PromptSimulacionResponseSchema,
  type FinalizarSimulacionRequest,
  type FinalizarSimulacionResponse,
  type IniciarSimulacionRequest,
  type IniciarSimulacionResponse,
  type MiSimulacionResponse,
  type MisAsignacionesResponse,
  type OkResponse,
  type PromptSimulacionResponse,
  type ResetearAsignacionRequest,
  type ResetearMiAsignacionRequest,
} from '@alter/types';
import type { HttpClient } from './httpClient.js';

/**
 * Endpoints REST de simulación: flujo del alumno (Paso 2, Paso 3, cierre de
 * negocio, autoreset) y lab tutor/admin.
 *
 * Todos los métodos requieren JWT (lo inyecta `HttpClient` desde
 * `TokenStorage`). El backend valida el rol; este cliente no impone
 * filtros de rol propios — la app gobierna qué pantalla llama qué endpoint.
 *
 * En valores de enum laxo (`estado`), devuelve la respuesta tal cual la
 * valida Zod. El caller usa los narrowing helpers de `@alter/types`
 * cuando le importa.
 */
export class SimulacionClient {
  constructor(private readonly http: HttpClient) {}

  // ---------- Cliente alumno ----------

  /**
   * Paso 2: `GET /api/mi-simulacion`. **No es de solo lectura**: crea la
   * simulación si no existe y marca `ABANDONADA` la que lleve más de 15 min
   * inactiva (nota de interfaz §7). Llamarlo una vez al entrar.
   *
   * Antes de `iniciar`, el caller valida `respuesta.audio` con
   * `verificarAudioDeclarado` (JC §0.2.1).
   */
  async getMiSimulacion(): Promise<MiSimulacionResponse> {
    return this.http.get('/api/mi-simulacion', MiSimulacionResponseSchema);
  }

  /** Paso 3: `POST /api/simulacion/iniciar`. Devuelve el ticket WS one-time (Paso 5). */
  async iniciar(req: IniciarSimulacionRequest): Promise<IniciarSimulacionResponse> {
    return this.http.post('/api/simulacion/iniciar', req, IniciarSimulacionResponseSchema);
  }

  /**
   * `POST /api/simulacion/finalizar` (JC §8): cierre de **negocio**. Va
   * después del cierre técnico del socket (`WebSocketClient.closeSession`),
   * y ninguno de los dos hace el trabajo del otro. `ya_finalizada: true` en
   * la respuesta es éxito (reintento idempotente).
   */
  async finalizar(req: FinalizarSimulacionRequest): Promise<FinalizarSimulacionResponse> {
    // Validación local antes de enviar: `simulacionId` o un motivo fuera
    // del catálogo responden 400.
    FinalizarSimulacionRequestSchema.parse(req);
    return this.http.post('/api/simulacion/finalizar', req, FinalizarSimulacionResponseSchema);
  }

  /** `POST /api/mi-asignacion/resetear`: autoreset, solo en asignaciones `es_prueba`. */
  async resetearMiAsignacion(req: ResetearMiAsignacionRequest): Promise<OkResponse> {
    return this.http.post('/api/mi-asignacion/resetear', req, OkResponseSchema);
  }

  // ---------- Lab tutor/admin (§3) ----------

  /** §3.1 `GET /api/mis-asignaciones` — sólo TUTOR/ADMINISTRADOR. */
  async getMisAsignaciones(): Promise<MisAsignacionesResponse> {
    return this.http.get('/api/mis-asignaciones', MisAsignacionesResponseSchema);
  }

  /** §3.2 `POST /api/asignacion/resetear` — reset desde lab. */
  async resetearAsignacion(req: ResetearAsignacionRequest): Promise<OkResponse> {
    return this.http.post('/api/asignacion/resetear', req, OkResponseSchema);
  }

  /**
   * §3.3 `GET /api/simulacion/prompt?simulacionId=…` — prompt del SP.
   * INV-05: nunca llamar desde contextos de alumno (el backend valida
   * el rol, pero el cliente tampoco debería pedirlo).
   */
  async getPrompt(simulacionId: string): Promise<PromptSimulacionResponse> {
    const path = `/api/simulacion/prompt?simulacionId=${encodeURIComponent(simulacionId)}`;
    return this.http.get(path, PromptSimulacionResponseSchema);
  }
}
